import type { UUID } from '@cobliteam/common-types/src/identifier';
import camelcaseKeys from 'camelcase-keys';
import HttpMethod from '../../request/HttpMethod';
import HttpStatusCode from '../../request/HttpStatusCode';
import Requester from '../../request/Requester';
import type { Configuration } from '../../request/Types';

export interface VehicleOdometerAtDate {
  odometerInKm: number;
  lastIncludedTripEndTime: number;
}

export class OdometerNoTelemetryError extends Error {
  constructor() {
    super('NO_TELEMETRY');
    this.name = 'OdometerNoTelemetryError';
  }
}

// The endpoint may answer 200 with an unusable payload. Callers treat that
// exactly like a 404: no pre-fill.

// Narrows to a keyed object — arrays are `typeof 'object'` too, and neither
// shape carries an odometer.
const isPlainRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

// The instant carries the badge and the divergence reference, so it has to be a
// real one. `odometer-api` serialises epoch 0 whenever its CAN branch has no
// event time, producing a 200 with `odometer_in_km: 0` under a 01/01/1970
// label. Zero km alone is NOT rejected: a vehicle whose only checkpoint is 0 km
// is a legitimate zero reading with a real instant.
const MIN_PLAUSIBLE_EPOCH_MS = Date.UTC(2000, 0, 1);

// Rejects absent, null and unparseable values. Numeric strings are accepted
// because the API serialises the odometer as a string.
const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export class OdometerRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  // GET /v1/odometer/{vehicleId}?date=<epochMs> — served by odometer-api via
  // herbie-bouncer under basic:permission. 404 signals no telemetry for the
  // vehicle at that instant; callers treat it as "no pre-fill" (not an error).
  public getVehicleOdometerAtDate = async (vehicleId: UUID, epochMs: number): Promise<VehicleOdometerAtDate> => {
    try {
      const response = await this.requester.request(
        {
          endpoint: `/v1/odometer/${vehicleId}`,
          method: HttpMethod.GET,
          params: { date: epochMs },
        },
        [HttpStatusCode.NOT_FOUND]
      );

      const payload: unknown = response.data;
      if (!isPlainRecord(payload)) {
        throw new OdometerNoTelemetryError();
      }

      const data = camelcaseKeys(payload, { deep: true }) as {
        odometerInKm?: unknown;
        lastIncludedTripEndTime?: unknown;
      };

      const odometerInKm = toFiniteNumber(data.odometerInKm);
      const lastIncludedTripEndTime = toFiniteNumber(data.lastIncludedTripEndTime);
      if (odometerInKm === null || lastIncludedTripEndTime === null || lastIncludedTripEndTime < MIN_PLAUSIBLE_EPOCH_MS) {
        throw new OdometerNoTelemetryError();
      }

      return { odometerInKm, lastIncludedTripEndTime };
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === HttpStatusCode.NOT_FOUND) {
        throw new OdometerNoTelemetryError();
      }
      throw error;
    }
  };
}

export default OdometerRequester;
